import { loadGoogleTagManager } from './internal/googleTagManager';
import { loadGoogleAnalytics } from './internal/googleAnalytics';
import { initJourney } from './internal/journey';
import { getDeployEnv } from './internal/deployEnv';
import { loadSegment } from './internal/segment';
import { buildDefaultDatadogOptions } from './internal/datadog';
import { ObservabilityOptions } from './declarations/Observability';
import { globalObservability } from './global';
import { defaultInitDatadogRumClient, defaultInitSentry } from './internal/builtin';
import { DebugObservability, RealObservability } from './observability-base';
import { buildDefaultSentryOpts } from './internal/sentry';
import { BaseJourneyEventWrapper } from './declarations/JourneyEvent';

/** Call this before you use useObservability or globalObservability. */
export function initObservability<
  AppCategory extends string | undefined = undefined,
  AppActions extends string | undefined = undefined,
  AppJourneyEventWrapper extends BaseJourneyEventWrapper = BaseJourneyEventWrapper,
>({
  appName,
  env,
  version,
  eventTrackingCategory,
  googleAnalyticsToken,
  sentry,
  datadog,
  debug,
  journeyEventHelpers,
}: ObservabilityOptions<AppCategory>): void {
  if (globalObservability.isInitialized()) {
    throw new Error('initObservability was already called');
  }

  env = env || getDeployEnv();
  if (env === 'development') {
    globalObservability.setCurrentInstance(
      new DebugObservability<AppCategory, AppActions, AppJourneyEventWrapper>({ log: debug }),
    );
  } else {
    // TODO: Hide these in observability/internal like initSentry.
    loadGoogleAnalytics(googleAnalyticsToken);
    loadGoogleTagManager();
    loadSegment();

    const realObservabilityInstance = new RealObservability<
      AppCategory,
      AppActions,
      AppJourneyEventWrapper
    >(eventTrackingCategory, journeyEventHelpers);
    const { eventClient, userClient } = initJourney();
    userClient && realObservabilityInstance.setJourneyUserClient(userClient);
    eventClient && realObservabilityInstance.setJourneyEventClient(eventClient);
    realObservabilityInstance.setSentryClient(
      defaultInitSentry({ ...buildDefaultSentryOpts({ appName, env, version }), ...sentry }),
    );
    realObservabilityInstance.setDatadogRumClient(
      defaultInitDatadogRumClient({
        ...buildDefaultDatadogOptions({ appName, env, version }),
        ...datadog,
      }),
    );
    globalObservability.setCurrentInstance(realObservabilityInstance);
  }
}

export * from './contextualData';
export * from './declarations';
export * from './react/ContextualDataContext';
export * from './react/useObservability';
export { Sentry, loadSentry } from './internal/builtin';
export { globalObservability } from './global';
export * from './constants';
export * from './observability-base';
export * as analytics from './internal/analytics';
